import React from 'react';
import { Avatar, Badge, Tooltip } from 'antd';

/**
 * PhotoThumb
 *
 * Compact table-cell control that shows the FIRST captured photo of a
 * reconciliation record as a square thumbnail (same visual style as the
 * master Asset list), with the exact photo count shown as a badge on top.
 * Clicking opens the full-screen gallery (handled by the parent via onClick).
 *
 * Data reliability:
 *  - Uses ONLY the inline `photos` already present on the record. It does NOT
 *    fetch anything (unlike the master-asset <LatestImage/>, which loads by
 *    asset id), so there are no extra per-row API calls and the images shown
 *    are exactly the ones captured against this AST/PV record.
 *  - The badge count is the exact length of the photo array. Zero photos is a
 *    real value (badge shows 0, greyed, not clickable) — never a placeholder.
 *
 * Props:
 *  - photos  : array -> [{ url: string }, ...] as stored on the record
 *  - onClick : fn    -> open handler, only fired when photos exist
 */
// Subtle count chip that sits on the thumbnail rather than a bright,
// notification-style badge.
const badgeStyle = {
  backgroundColor: 'rgba(0,0,0,0.55)',
  color: '#fff',
  boxShadow: 'none',
  fontSize: 10,
  height: 15,
  lineHeight: '15px',
  minWidth: 15,
  padding: '0 4px',
  borderRadius: 8,
};

const PhotoThumb = ({ photos, onClick }) => {
  const images = Array.isArray(photos) ? photos.filter(p => p && p.url) : [];
  const count = images.length;
  const first = images[0];

  if (!count) {
    // No photos captured for this record — muted placeholder avatar, no badge.
    return (
      <Tooltip title="No photos">
        <Avatar shape="square" size={55} icon="file-image" style={{ opacity: 0.4 }} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`View ${count} photo${count > 1 ? 's' : ''}`}>
      <Badge count={count} overflowCount={999} style={badgeStyle}>
        <Avatar
          shape="square"
          size={55}
          icon="file-image"
          src={first.url}
          alt={`Photo 1 of ${count}`}
          style={{ cursor: 'pointer' }}
          onClick={onClick}
        />
      </Badge>
    </Tooltip>
  );
};

export default PhotoThumb;
